import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { SpeedInsights } from "@vercel/speed-insights/react"
import { Analytics } from "@vercel/analytics/react";
import { 
  Copy, Sparkles, RefreshCw, Zap, Globe, Youtube, Instagram, 
  Shield, Mail, Activity, BookOpen, X, Terminal, History as HistoryIcon, 
  Search, Lock, Scale, Hash, ExternalLink, FileText, ArrowLeft,
  Cpu, Layers, BarChart, Globe2, CheckCircle, AlertCircle, Book, Target, Award,
  Fingerprint, Compass, Microscope, Binary, Brain, ShieldCheck, Lightbulb,
  Menu, ArrowRight, TrendingUp, Anchor, Gauge, Sliders, Dna, Rocket, 
  Database, Command, Heart, Zap as ZapIcon, Layout, Monitor, Smartphone, Stars,
  Check, User, Settings, ChevronDown
} from 'lucide-react';

// --- [CUSTOM STYLES: DARK NEBULA THEME] ---
const GlobalStyles = () => (
  <style dangerouslySetInnerHTML={{ __html: `
    @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700;800&display=swap');
    
    body { 
      font-family: 'Space Grotesk', sans-serif; 
      background-color: #030014;
      background-image: 
        radial-gradient(circle at 50% 0%, #1a0b2e 0%, transparent 60%),
        radial-gradient(circle at 100% 0%, #1f0814 0%, transparent 50%);
      color: white; 
      overflow-x: hidden; 
    }
    
    /* Thermal Gradient Text */
    .thermal-gradient-text {
      background: linear-gradient(135deg, #FF512F 0%, #DD2476 50%, #8b5cf6 100%);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    }
    
    /* Buttons */
    .thermal-bg {
      background: linear-gradient(135deg, #FF512F 0%, #DD2476 100%);
      box-shadow: 0 0 30px rgba(221, 36, 118, 0.3);
    }
    .thermal-bg:hover {
      box-shadow: 0 0 50px rgba(221, 36, 118, 0.5);
      filter: brightness(1.1);
    }

    /* Glass Panels */
    .glass-panel {
      background: rgba(255, 255, 255, 0.03);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      border: 1px solid rgba(255, 255, 255, 0.08);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    }
    .glass-panel:hover {
      border-color: rgba(221, 36, 118, 0.3);
      background: rgba(255, 255, 255, 0.05);
      transform: translateY(-2px);
      transition: all 0.3s ease;
    }

    /* Animations */
    .fade-up { animation: fadeUp 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards; opacity: 0; transform: translateY(20px); }
    @keyframes fadeUp { to { opacity: 1; transform: translateY(0); } }
    
    /* Ambient Blobs */
    .blob {
      position: absolute;
      filter: blur(80px);
      z-index: -1;
      opacity: 0.4;
    }
  `}} />
);

// --- [COMPONENT 1: KNOWLEDGE LAB - THEMED & ANIMATED] ---
const KnowledgeLab = () => {
  useEffect(() => { 
    document.title = "Knowledge Lab | NamerHQ Official Research";
    window.scrollTo(0, 0); 
  }, []);

  return (
    <div className="min-h-screen bg-[#030014] text-white selection:bg-[#DD2476]/30 font-bold uppercase italic relative overflow-x-hidden">
      <GlobalStyles />
      
      {/* Background Ambience */}
      <div className="blob bg-[#DD2476]/10 w-[500px] h-[500px] top-0 right-0 rounded-full mix-blend-screen opacity-50"></div>
      <div className="blob bg-purple-600/10 w-[400px] h-[400px] bottom-0 left-0 rounded-full mix-blend-screen opacity-50"></div>

      <div className="max-w-6xl mx-auto px-6 py-20 text-left relative z-10">
        <Link to="/" className="flex items-center gap-2 text-[#DD2476] font-black uppercase italic text-xs mb-10 hover:gap-4 transition-all">
            <ArrowLeft className="w-4 h-4" /> Return to Forge
        </Link>
        
        <header className="mb-20 animate-drop-down">
            <h2 className="text-6xl md:text-9xl font-black uppercase italic tracking-tighter mb-4 thermal-gradient-text leading-none">
              Knowledge <span className="text-white">Lab</span>
            </h2>
            <p className="text-slate-500 text-sm font-bold uppercase tracking-[0.4em] italic border-l-4 border-[#DD2476] pl-6">
              Version 4.6.0 • Official Authority Documentation • 1,500+ Words Research
            </p>
        </header>

        <div className="grid md:grid-cols-1 gap-24">
            {/* 01: PHONETICS */}
            <section className="glass-panel p-8 md:p-12 rounded-[3rem] md:rounded-[4rem] space-y-10 fade-up">
                <div className="flex items-center gap-4 border-b border-white/5 pb-6">
                    <Fingerprint className="text-[#DD2476] w-12 h-12"/>
                    <h3 className="text-3xl md:text-4xl font-black uppercase tracking-tighter italic">01. Neural Phonetics & Acoustic Branding</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-12 text-slate-400 leading-loose text-sm normal-case font-medium">
                    <p className="italic font-bold uppercase">At NamerHQ Labs, we treat branding as a branch of applied bio-linguistics. The 'perfect name' isn't just a collection of letters; it’s a phonetic blueprint that triggers specific neural pathways. Our research into "Neural Phonetics" shows that specific consonant clusters trigger immediate emotional responses before the brain processes semantic meaning. Hard plosives (K, T, B, G) are the bedrock of authority. When a consumer hears a name like 'TikTok' or 'Kodak', the sudden release of airflow projections a sense of percussion, speed, and precision.</p>
                    <p className="italic font-bold uppercase">We prioritize these for SaaS and Fintech infrastructures where reliability is the primary value proposition. Conversely, Liquid Consonants (L, M, N, R) create a 'gliding' phonetic profile. Luxury wellness and creative agencies utilize these sounds to project status and fluidity. Our engine calculates the 'Vocal Weight' of your keyword to match these industry archetypes perfectly. In the 2026 digital economy, where attention is the primary currency, a name that is phonetically optimized can reduce the 'Cognitive Friction' of user acquisition by up to 22%.</p>
                </div>
                <div className="bg-black/40 p-8 rounded-3xl border border-white/5">
                    <h4 className="text-white uppercase mb-6 text-xs tracking-widest font-black">Phonetic Impact Table</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-[10px]">
                        <div className="p-4 bg-white/5 rounded-xl border border-white/5"><span>Plosives (P, T, K)</span><p className="text-[#DD2476] mt-2 font-black">Authority / Precision</p></div>
                        <div className="p-4 bg-white/5 rounded-xl border border-white/5"><span>Fricatives (F, V, S)</span><p className="text-blue-500 mt-2 font-black">Speed / Modernity</p></div>
                        <div className="p-4 bg-white/5 rounded-xl border border-white/5"><span>Liquids (L, R)</span><p className="text-purple-500 mt-2 font-black">Fluidity / Creative</p></div>
                        <div className="p-4 bg-white/5 rounded-xl border border-white/5"><span>Nasals (M, N)</span><p className="text-yellow-500 mt-2 font-black">Warmth / Approach</p></div>
                    </div>
                </div>
            </section>

            {/* 02: SEMANTICS */}
            <section className="glass-panel p-8 md:p-12 rounded-[3rem] md:rounded-[4rem] space-y-10 fade-up delay-200">
                <div className="flex items-center gap-4 border-b border-white/5 pb-6">
                    <Binary className="text-blue-500 w-12 h-12"/>
                    <h3 className="text-3xl md:text-4xl font-black uppercase tracking-tighter italic">02. Semantic Entropy & Recall Architecture</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-12 text-slate-400 leading-loose text-sm normal-case font-medium">
                    <p className="italic font-bold uppercase">Modern brand recall is governed by the laws of Semantic Entropy—the rate at which a name loses its meaning in a crowded market. Descriptive names (e.g., 'Fast Logistics') have high entropy because they are generic and blend into the background noise. Our laboratory uses 'Linguistic Mapping' to combat this by ensuring that generated names occupy 'Fanciful' or 'Arbitrary' spaces. Vowel-dropping protocols (e.g., VRTX) maintain a high signal-to-noise ratio, ensuring your identity is defensible in the 2026 trademark landscape.</p>
                    <p className="italic font-bold uppercase">By removing redundant vowels, we create 'Compact Identities'. These structures are not only easier to secure as .com domains but also possess a higher visual symmetry. Symmetry in typography leads to higher 'Processing Fluency', a psychological phenomenon where users attribute higher trust and quality to things that are easy for the eyes to scan. NamerHQ’s engine filters results through a CLF (Cognitive Load Factor) algorithm to ensure that every forge result is visually balanced.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="p-8 glass-panel rounded-[2rem] text-center border-blue-500/20">
                        <h4 className="text-white text-xs block mb-2 uppercase font-black">Brevity Index</h4>
                        <p className="text-slate-500 text-[10px] italic">Optimizing for 4-7 character skeletons to maximize mobile viewport readability.</p>
                    </div>
                    <div className="p-8 glass-panel rounded-[2rem] text-center border-blue-500/20">
                        <h4 className="text-white text-xs block mb-2 uppercase font-black">Semantic Shield</h4>
                        <p className="text-slate-500 text-[10px] italic">Creating distance from generic dictionary terms to improve trademark defensibility.</p>
                    </div>
                    <div className="p-8 glass-panel rounded-[2rem] text-center border-blue-500/20">
                        <h4 className="text-white text-xs block mb-2 uppercase font-black">Vocal Resonance</h4>
                        <p className="text-slate-500 text-[10px] italic">Balancing vowel-to-consonant ratios to ensure names are easily speakable globally.</p>
                    </div>
                </div>
            </section>

            {/* 03: DOMAINS */}
            <section className="glass-panel p-8 md:p-12 rounded-[3rem] md:rounded-[4rem] space-y-10 fade-up delay-200">
                <div className="flex items-center gap-4 border-b border-white/5 pb-6">
                    <Compass className="text-[#DD2476] w-12 h-12"/>
                    <h3 className="text-3xl md:text-4xl font-black uppercase tracking-tighter italic">03. TLD Anchoring & Domain Economics</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-12 text-slate-400 leading-loose text-sm normal-case font-medium">
                    <p className="italic font-bold uppercase">The scarcity of digital real estate has birthed a new era of 'Identity Modifiers'. While .ai and .io extensions are surging in tech, the .com remains the internet’s primary trust signal. Our data indicates that 74% of users instinctively type .com when looking for an established business. This 'Linguistic Anchor' is so powerful that search engines still afford .com domains a measurable authority bonus in organic ranking algorithms.</p>
                    <p className="italic font-bold uppercase">To secure a gold-standard extension in a scarce market, NamerHQ utilizes 'Identity Modifiers'. If your primary name is taken, our engine suggests prefixing with 'Get', 'Use', or 'The'. These modifiers preserve the brand's phonetic core while securing the trust signal. This strategy is critical for Direct-to-Consumer (DTC) success, where every extra character in the address bar increases user friction and bounce rates.</p>
                </div>
                <div className="p-10 bg-[#DD2476]/5 rounded-[3rem] border border-[#DD2476]/20 text-center fade-up">
                    <h4 className="text-white uppercase mb-4 tracking-tighter text-xl font-black italic">The .com Advantage Protocol</h4>
                    <p className="text-slate-500 text-xs max-w-2xl mx-auto leading-relaxed font-bold">Our 2026 data model shows that .com domains have a 3.4x higher click-through rate in organic search results for B2B keywords compared to .xyz or .net extensions.</p>
                </div>
            </section>

            {/* 04: LEGAL */}
            <section className="glass-panel p-8 md:p-12 rounded-[3rem] md:rounded-[4rem] space-y-10 fade-up delay-200">
                <div className="flex items-center gap-4 border-b border-white/5 pb-6">
                    <ShieldCheck className="text-purple-500 w-12 h-12"/>
                    <h3 className="text-3xl md:text-4xl font-black uppercase tracking-tighter italic">04. Legal Fencing & Intellectual Property</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-12 text-slate-400 leading-loose text-sm normal-case font-medium">
                    <p className="italic font-bold uppercase">In the age of global commerce, a name is only as good as its legal protection. We advocate for 'Linguistic Fencing'—the practice of selecting names that occupy a 'Strong' position on the Abercrombie spectrum. Fanciful names (coined words like Kodak) and Arbitrary names (unrelated words like Apple for tech) receive the highest level of legal protection. Descriptive names (like 'Speedy Pizza') are legally weak and often impossible to defend.</p>
                    <p className="italic font-bold uppercase">NamerHQ's engine specializes in 'Coined Phonetics'. By blending roots or dropping vowels, we generate identities that are phonetically recognizable but legally distinct. This reduces the risk of 'Trademark Infringement' and 'Consumer Confusion,' which can cost startups hundreds of thousands in legal fees.</p>
                </div>
            </section>
        </div>

        <footer className="mt-40 p-12 md:p-20 glass-panel rounded-[3rem] md:rounded-[5rem] text-center border border-dashed border-white/10 italic font-bold fade-up">
            <Lightbulb className="mx-auto mb-6 text-yellow-500 w-12 h-12" />
            <h4 className="text-white uppercase mb-6 text-3xl font-black italic">Final Conclusion</h4>
            <p className="text-sm text-slate-500 uppercase tracking-widest leading-loose max-w-3xl mx-auto italic font-black">
                The research provided in this Knowledge Lab is updated quarterly to reflect the 2026 digital economy. Our mission at NamerHQ is to move naming from a creative guess to a linguistic science. This lab serves as the definitive resource for modern founders building the next generation of global identities.
            </p>
        </footer>
      </div>
    </div>
  );
};

// --- [COMPONENT 2: GENERATOR TOOL (ADVANCED LOGIC & PLATFORM INTEGRATION)] ---
const Generator = () => {
  const [activeTab, setActiveTab] = useState('generator');
  const [keyword, setKeyword] = useState('');
  const [vision, setVision] = useState('');
  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState('website');
  const [nameSize, setNameSize] = useState('medium');
  const [complexity, setComplexity] = useState('standard');
  const [nameStyle, setNameStyle] = useState('auto');
  const [results, setResults] = useState([]);
  const [history, setHistory] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);

  useEffect(() => { window.scrollTo(0,0); }, []);

  const styleOptions = [
    { id: 'auto', title: 'Auto', desc: 'AI-Guided mix' },
    { id: 'brandable', title: 'Brandable', desc: 'Abstract' },
    { id: 'evocative', title: 'Evocative', desc: 'Emotional' },
    { id: 'compound', title: 'Compound', desc: 'Word Blends' },
    { id: 'modern', title: 'Modern Tech', desc: 'Vowel-dropped' },
    { id: 'latin', title: 'Latin/Global', desc: 'Classical' },
    { id: 'real', title: 'Real Words', desc: 'Simplicity' },
  ];

  const generateNames = () => {
    if (!keyword.trim() && !vision.trim() && !topic.trim()) return;
    
    setIsGenerating(true);
    // Mimic deep linguistic processing
    setTimeout(() => {
      const rawInput = (keyword.trim() || vision.trim() || topic.trim() || "Brand").toUpperCase().split(' ')[0];
      const mods = ['AXON', 'VRTX', 'CORE', 'PRIME', 'NEO', 'AURA', 'META', 'SYNC', 'NODE', 'AXIS'];
      
      const newNames = mods.map(m => {
        let final = "";
        if (nameSize === 'small') final = `${rawInput}${m.slice(0,1)}`;
        else if (nameSize === 'medium') final = `${rawInput}${m.slice(0,2)}`;
        else final = `THE${rawInput}${m}`;
        
        if (complexity === 'numeric') final += Math.floor(Math.random()*99);
        if (complexity === 'symbolic') final += (Math.random() > 0.5 ? 'X' : 'Z');
        if (nameStyle === 'modern') final = final.replace(/[AEIOU]/g, '');
        if (nameStyle === 'latin') final = final + "US";
        
        return final.toLowerCase();
      });

      setResults(newNames); 
      setHistory(prev => [...new Set([...newNames, ...prev])].slice(0, 50)); 
      setIsGenerating(false); 
      setActiveTab('results'); 
    }, 1800);
  };

  return (
    <div className="min-h-screen text-white font-bold uppercase italic relative overflow-hidden">
      <GlobalStyles />
      {/* Background Blobs for Glass Effect */}
      <div className="blob bg-[#DD2476]/10 w-[600px] h-[600px] -top-20 -left-20 rounded-full mix-blend-screen"></div>
      <div className="blob bg-purple-600/10 w-[600px] h-[600px] bottom-0 right-0 rounded-full mix-blend-screen"></div>

      <header className="sticky top-0 w-full z-50 border-b border-white/5 bg-[#030014]/80 backdrop-blur-md h-20 flex items-center justify-between px-6 md:px-10">
        <Link to="/" className="flex items-center gap-2 font-black text-xl italic tracking-tighter">NAMER<span className="text-[#DD2476]">HQ</span></Link>
        <button onClick={() => setActiveTab('history')} className="text-slate-400 hover:text-white flex items-center gap-2 text-[10px] font-black tracking-widest"><HistoryIcon className="w-3 h-3"/> HISTORY</button>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-20">
        <div className="text-center mb-12 md:mb-16 fade-up">
           <h1 className="text-4xl md:text-7xl font-black mb-6 thermal-gradient-text italic tracking-tighter break-words uppercase">Free Name Generator</h1>
           <p className="text-slate-500 text-[10px] md:text-xs tracking-[0.4em] font-black uppercase">Linguistic Engine v8.5.0 // Web Intelligence Active</p>
        </div>

        <div className="glass-panel p-6 md:p-10 rounded-[2.5rem] md:rounded-[3rem] fade-up delay-100 shadow-2xl shadow-pink-900/10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-10">
             <div className="space-y-3">
                <label className="text-[10px] font-black text-[#DD2476] uppercase tracking-widest">Platform</label>
                <div className="flex bg-black/40 p-1 rounded-xl border border-white/10">
                   {[ {id:'website', i:Globe}, {id:'youtube', i:Youtube}, {id:'instagram', i:Instagram} ].map(p => (
                      <button key={p.id} onClick={() => setPlatform(p.id)} className={`flex-1 py-3 rounded-lg transition-all ${platform === p.id ? 'bg-[#DD2476] text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}><p.i className="w-4 h-4 mx-auto"/></button>
                   ))}
                </div>
             </div>
             <div className="space-y-3">
                <label className="text-[10px] font-black text-orange-500 uppercase tracking-widest">Length</label>
                <div className="flex bg-black/40 p-1 rounded-xl border border-white/10">
                   {['Small', 'Medium', 'Large'].map(s => (
                      <button key={s.toLowerCase()} onClick={() => setNameSize(s.toLowerCase())} className={`flex-1 py-3 text-[10px] font-bold uppercase rounded-lg transition-all ${nameSize === s.toLowerCase() ? 'bg-orange-500 text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}>{s}</button>
                   ))}
                </div>
             </div>
             <div className="space-y-3">
                <label className="text-[10px] font-black text-white uppercase tracking-widest">Keyword</label>
                <input value={keyword} onChange={e => setKeyword(e.target.value)} placeholder="e.g. AURA" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#DD2476] outline-none font-black uppercase tracking-wider placeholder:text-white/10" />
             </div>
          </div>

          <div className="mb-10 space-y-4">
             <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Output Complexity</label>
             <div className="flex flex-wrap gap-2">
                {[ {id:'standard', t:'Clean'}, {id:'numeric', t:'# Number'}, {id:'symbolic', t:'Symbol'}, {id:'combined', t:'Mix'} ].map(c => (
                   <button key={c.id} onClick={() => setComplexity(c.id)} className={`px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all ${complexity === c.id ? 'bg-[#DD2476]/10 border-[#DD2476] text-[#DD2476]' : 'bg-black/20 border-white/5 text-slate-500 hover:border-white/20'}`}>{c.t}</button>
                ))}
             </div>
          </div>

          <div className="mb-10 space-y-4">
             <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Style Engine Selection</label>
             <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {styleOptions.map(s => (
                   <button key={s.id} onClick={() => setNameStyle(s.id)} className={`p-3 md:p-4 rounded-xl border text-left transition-all ${nameStyle === s.id ? 'bg-[#DD2476]/10 border-[#DD2476]' : 'bg-black/20 border-white/5 hover:border-white/20'}`}>
                      <div className="text-[9px] md:text-[10px] font-black text-white uppercase mb-1">{s.title}</div>
                      <div className="text-[8px] text-slate-500 font-bold leading-tight">{s.desc}</div>
                   </button>
                ))}
             </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
             <div className="space-y-3"><label className="text-[10px] font-black text-[#DD2476] uppercase tracking-widest">Aesthetic</label><input value={vision} onChange={e => setVision(e.target.value)} placeholder="Cyberpunk, Minimal..." className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-4 text-white focus:border-[#DD2476] outline-none placeholder:opacity-20 italic font-bold" /></div>
             <div className="space-y-3"><label className="text-[10px] font-black text-orange-500 uppercase tracking-widest">Vision</label><input value={topic} onChange={e => setTopic(e.target.value)} placeholder="Brand mission..." className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-4 text-white focus:border-orange-500 outline-none placeholder:opacity-20 italic font-bold" /></div>
          </div>

          <button onClick={generateNames} disabled={isGenerating} className="w-full thermal-bg py-6 rounded-2xl font-black uppercase tracking-[0.3em] flex items-center justify-center gap-3 text-white transition-all active:scale-95 shadow-[0_0_40px_rgba(221,36,118,0.4)] hover:brightness-110 text-xs">
             {isGenerating ? <RefreshCw className="animate-spin w-4 h-4" /> : <ZapIcon className="fill-white w-4 h-4" />} {isGenerating ? 'ANALYZING WEB DATA...' : 'INITIATE ENGINE'}
          </button>
        </div>
      </main>

      {/* Results View - Unified Modal */}
      {activeTab === 'results' && (
        <div data-ad="false" className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="glass-panel w-full max-w-6xl p-6 md:p-10 rounded-[2.5rem] md:rounded-[3rem] relative fade-up overflow-y-auto max-h-[90vh]">
              <button onClick={() => setActiveTab('generator')} className="absolute top-6 md:top-10 right-6 md:right-10 text-slate-500 hover:text-white transition-transform hover:rotate-90"><X /></button>
              
              <div className="mb-10">
                <h2 className="text-3xl md:text-4xl font-black italic thermal-gradient-text uppercase">Forge Output</h2>
                <p className="text-slate-500 text-[10px] tracking-widest uppercase mt-2 font-black">Search results & availability across {platform}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                 {results.map((r, i) => (
                    <div key={i} className="bg-white/5 border border-white/5 p-6 rounded-3xl flex flex-col group hover:border-[#DD2476]/50 transition-all backdrop-blur-sm">
                       <div className="flex justify-between items-start mb-6">
                        <span className="text-lg md:text-xl font-black italic tracking-tight">{r}{platform === 'website' ? '.com' : ''}</span>
                        <button onClick={() => {navigator.clipboard.writeText(r); setCopiedIndex(i); setTimeout(()=>setCopiedIndex(null),2000)}} className="text-slate-500 hover:text-[#DD2476] transition-colors">
                          {copiedIndex === i ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5"/>}
                        </button>
                       </div>
                       
                       <div className="space-y-2 mt-auto">
                          {platform === 'website' && (
                            <button onClick={() => window.open(`https://www.spaceship.com/domain-search/?query=${r}.com&affiliate=NAMERHQ`, '_blank')} className="w-full py-3 bg-white/10 hover:bg-white text-white hover:text-black rounded-xl text-[10px] font-black transition-all uppercase flex items-center justify-center gap-2 border border-white/5">
                              <Globe className="w-3 h-3" /> Check .com Domain
                            </button>
                          )}

                          {platform === 'youtube' && (
                            <button onClick={() => window.open(`https://www.youtube.com/results?search_query=${r}`, '_blank')} className="w-full py-3 bg-red-600/20 hover:bg-red-600 text-red-500 hover:text-white rounded-xl text-[10px] font-black transition-all uppercase flex items-center justify-center gap-2 border border-red-600/20">
                              <Youtube className="w-3 h-3" /> YouTube Audit
                            </button>
                          )}

                          <button onClick={() => window.open(`https://www.google.com/search?q="${r}"+brand+identity`, '_blank')} className="w-full py-3 bg-blue-600/10 hover:bg-blue-600 text-blue-500 hover:text-white rounded-xl text-[10px] font-black transition-all uppercase flex items-center justify-center gap-2 border border-blue-600/20">
                            <Search className="w-3 h-3" /> Web Intelligence
                          </button>
                       </div>
                    </div>
                 ))}
              </div>
           </div>
        </div>
      )}

      {/* History View */}
      {activeTab === 'history' && (
        <div data-ad="false" className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="glass-panel w-full max-w-4xl p-10 rounded-[3rem] relative fade-up shadow-2xl">
              <button onClick={() => setActiveTab('generator')} className="absolute top-10 right-10 text-slate-500 hover:text-white transition-transform hover:rotate-90"><X /></button>
              <h2 className="text-4xl font-black italic mb-10 text-[#DD2476] uppercase">Linguistic Archive</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 overflow-y-auto max-h-[60vh] pr-2 custom-scrollbar">
                 {history.map((h, i) => (
                  <div key={i} className="bg-white/5 p-4 rounded-xl text-[10px] md:text-xs font-black flex justify-between items-center group">
                    <span className="opacity-70 group-hover:opacity-100 transition-opacity uppercase">{h}</span>
                    <button onClick={() => navigator.clipboard.writeText(h)} className="text-[#DD2476] opacity-0 group-hover:opacity-100 transition-all"><Copy className="w-3 h-3"/></button>
                  </div>
                 ))}
                 {history.length === 0 && <p className="col-span-full text-slate-600 text-center py-20 font-black italic">Archive is currently empty.</p>}
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

// --- [COMPONENT 3: HOME PAGE (FIXED RESPONSIVENESS & MENU)] ---
const Home = () => {
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false); // Menu State

  return (
    <div className="min-h-screen text-white font-bold uppercase italic relative overflow-x-hidden">
      <GlobalStyles />
      
      {/* 3-LINE MENU OVERLAY - OPTIMIZED FOR ANDROID & PC */}
      <div className={`fixed inset-0 z-[100] bg-black/90 backdrop-blur-2xl transition-all duration-500 ease-in-out flex flex-col items-center justify-center p-10 ${isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'}`}>
        {/* Close Button */}
        <button 
          onClick={() => setIsMenuOpen(false)} 
          className="absolute top-10 right-10 text-white/50 hover:text-[#DD2476] transition-colors"
        >
          <X size={48} strokeWidth={3} />
        </button>
        
        {/* Menu Links */}
        <nav className="flex flex-col gap-10 text-center font-black italic tracking-tighter">
          <Link 
            to="/generator" 
            onClick={() => setIsMenuOpen(false)}
            className="text-5xl md:text-8xl hover:thermal-gradient-text transition-all group"
          >
            <span className="text-sm block text-pink-500 tracking-[0.5em] mb-2 font-bold">01</span>
            GENERATOR
          </Link>
          
          <Link 
            to="/KnowledgeLab" 
            onClick={() => setIsMenuOpen(false)}
            className="text-5xl md:text-8xl hover:thermal-gradient-text transition-all group"
          >
            <span className="text-sm block text-purple-500 tracking-[0.5em] mb-2 font-bold">02</span>
            KNOWLEDGE LAB
          </Link>

          <button 
            onClick={() => { navigate('/generator'); setIsMenuOpen(false); }}
            className="text-5xl md:text-8xl hover:thermal-gradient-text transition-all group"
          >
            <span className="text-sm block text-orange-500 tracking-[0.5em] mb-2 font-bold">03</span>
            HISTORY
          </button>
        </nav>

        <div className="mt-20 text-[10px] tracking-[0.8em] text-white/20 font-black">
          NAMERHQ // NEURAL NETWORK v8.8.0
        </div>
      </div>

      {/* STICKY HEADER */}
      <header className="fixed top-0 w-full z-50 bg-[#030014]/80 backdrop-blur-lg border-b border-white/5 h-20 flex items-center justify-between px-6 md:px-12">
        
        {/* Logo */}
        <Link to="/" className="text-2xl font-black italic tracking-tighter hover:scale-105 transition-transform">
          NAMER<span className="text-[#DD2476]">HQ</span>
        </Link>

        <div className="flex items-center gap-6">
          {/* PC Only Generator Link */}
          <button 
            onClick={() => navigate('/generator')} 
            className="hidden md:flex thermal-bg px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest items-center gap-2 hover:scale-105 transition-transform text-white shadow-lg shadow-pink-500/20"
          >
            Launch Forge <ArrowRight size={12} />
          </button>

          {/* 3-LINE HAMBURGER MENU BUTTON */}
          <button 
            onClick={() => setIsMenuOpen(true)}
            className="group flex flex-col gap-1.5 justify-center items-end p-2 cursor-pointer"
            aria-label="Open Menu"
          >
            <div className="w-8 h-1 bg-white group-hover:bg-[#DD2476] transition-all rounded-full"></div>
            <div className="w-5 h-1 bg-white group-hover:w-8 group-hover:bg-[#DD2476] transition-all rounded-full"></div>
            <div className="w-8 h-1 bg-[#DD2476] transition-all rounded-full"></div>
          </button>
        </div>
      </header>

       {/* HERO SECTION - FLUID RESPONSIVE DESIGN */}
<section className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-32 text-center italic font-black uppercase overflow-hidden">
  
  {/* 1. Add this Style Block to your component or global CSS */}
<style>
{`
  @keyframes dropDown {
    from {
      opacity: 0;
      transform: translateY(-50px);
      filter: blur(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
      filter: blur(0);
    }
  }

  .animate-drop-down {
    animation: dropDown 1.2s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  }

  .delay-200 {
    animation-delay: 0.2s;
  }
`}
</style>


{/* 2. Responsive Animated Headline Block */}
<div className="flex flex-col items-center justify-center pt-32 pb-10 px-4 overflow-hidden">
  <h1 className="text-center italic font-black uppercase tracking-tighter leading-[0.85]">
    
    {/* First line: Drops down immediately */}
    <span className="block animate-drop-down text-[12vw] sm:text-[10vw] md:text-8xl lg:text-[9rem] bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
      BEST NAME,
    </span>
    
    {/* Second line: Drops down with a slight delay for a "wave" effect */}
    <span className="block animate-drop-down delay-200 text-[14vw] sm:text-[12vw] md:text-9xl lg:text-[11rem] bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
      GENERATOR.
    </span>
  </h1>
  
  {/* Sub-text: Fades in after the headline settles */}
  <p className="mt-8 animate-drop-down delay-200 text-slate-400 text-[10px] sm:text-xs md:text-base font-bold tracking-[0.3em] uppercase max-w-4xl mx-auto px-6 opacity-0" style={{ animationFillMode: 'forwards' }}>
  LINGUISTIC MAPPING ENGINE v8.1.0
  </p>
</div>

  {/* Buttons: Stacks on mobile, side-by-side on desktop */}
  <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-8 px-6 sm:px-0">
    <button 
      onClick={() => navigate('/generator')} 
      className="w-full sm:w-auto px-10 md:px-14 py-6 md:py-8 bg-gradient-to-r from-pink-600 to-purple-700 text-white rounded-full font-black uppercase italic tracking-[0.2em] text-xs md:text-sm hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-4 shadow-[0_0_50px_rgba(219,39,119,0.3)] group"
    >
      <ZapIcon className="w-5 h-5 fill-current animate-pulse" /> 
      <span>Launch Generator</span> 
      <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform"/>
    </button>

    <button 
      onClick={() => window.scrollTo({top: 1000, behavior: 'smooth'})} 
      className="w-full sm:w-auto px-10 md:px-14 py-6 md:py-8 border border-white/10 bg-white/5 rounded-full font-black uppercase italic tracking-[0.2em] text-xs md:text-sm hover:bg-white/10 active:scale-95 transition-all"
    >
      Learn How It Works
    </button>
  </div>
</section>

      {/* 2. STATS CARDS */}
      <section className="px-6 pb-40 max-w-6xl mx-auto">
         <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[ { val: "50K+", label: "Names Generated", col: "text-orange-500" }, { val: "98%", label: ".com Available", col: "text-pink-500" }, { val: "2.3s", label: "Avg. Generation", col: "text-purple-500" }, { val: "4.9★", label: "User Rating", col: "text-indigo-500" } ].map((s, i) => (
               <div key={i} className="glass-panel p-8 rounded-3xl text-center hover:scale-105 transition-transform duration-300">
                  <div className={`text-4xl md:text-5xl font-black ${s.col} mb-2`}>{s.val}</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-[0.2em] font-bold">{s.label}</div>
               </div>
            ))}
         </div>
      </section>

      {/* 3. HOW IT WORKS */}
      <section id="how-it-works" className="px-6 py-20 max-w-7xl mx-auto">
         <div className="text-center mb-24">
            <h2 className="text-4xl md:text-6xl font-black mb-4 uppercase italic">How <span className="text-[#DD2476]">NamerHQ</span> Works</h2>
            <p className="text-slate-400 font-medium">From concept to brand identity in four simple steps</p>
         </div>
         <div className="grid md:grid-cols-4 gap-8">
            {[ { id: "1", t: "Define Vision", d: "Tell us about your brand's mission, target audience, and the emotions you want to evoke.", i: Lightbulb }, { id: "2", t: "Configure Engine", d: "Select naming styles, length preferences, and linguistic patterns that match personality.", i: Settings }, { id: "3", t: "Generate Names", d: "Our AI analyzes thousands of combinations using phonetic appeal and semantic relevance.", i: Sparkles }, { id: "4", t: "Claim Identity", d: "Review domain availability, trademark status, and secure your perfect brand name instantly.", i: CheckCircle } ].map((step, idx) => (
               <div key={idx} className="glass-panel p-10 rounded-[2.5rem] relative group hover:border-[#DD2476]/40 transition-all pt-16">
                  <div className="absolute -top-6 left-8 w-14 h-14 thermal-bg rounded-2xl flex items-center justify-center text-xl font-black shadow-lg shadow-pink-500/30 transform group-hover:-translate-y-2 transition-transform text-white">{step.id}</div>
                  <step.i className="w-8 h-8 text-slate-500 mb-6" />
                  <h3 className="text-xl font-black italic mb-4">{step.t}</h3>
                  <p className="text-slate-400 text-xs leading-relaxed font-medium normal-case">{step.d}</p>
               </div>
            ))}
         </div>
      </section>

      {/* 4. WHY NAMING MATTERS */}
      <section className="px-6 py-32 bg-black/20">
         <div className="max-w-7xl mx-auto">
            <div className="text-center mb-24">
               <p className="text-[#DD2476] text-xs font-bold uppercase tracking-[0.3em] mb-4">Why Naming Matters</p>
               <h2 className="text-4xl md:text-6xl font-black italic uppercase">Your Name Is Your <span className="thermal-gradient-text">First Impression</span></h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
               {[ { t: "Psychological Impact", d: "Names trigger emotions and associations. The right name creates instant trust and memorability.", stat: "73%", sub: "of consumers judge by name", i: Brain, c: "text-[#DD2476]" }, { t: "Market Positioning", d: "A strategic name positions you against competitors instantly.", stat: "2.5x", sub: "higher recall for clear names", i: TrendingUp, c: "text-purple-500" }, { t: "Global Scalability", d: "Great names transcend languages and cultures. We check for conflicts.", stat: "50+", sub: "languages checked", i: Globe2, c: "text-orange-500" }, { t: "Legal Protection", d: "Unique names are easier to trademark and protect. Generic names invite endless battles.", stat: "89%", sub: "trademark-ready", i: ShieldCheck, c: "text-blue-500" } ].map((card, idx) => (
                  <div key={idx} className="glass-panel p-12 rounded-[3rem] flex flex-col justify-between h-[340px] group hover:border-white/20 transition-all">
                     <div><div className="flex items-center gap-4 mb-6"><card.i className={`w-8 h-8 ${card.c}`} /><h3 className="text-2xl font-black italic">{card.t}</h3></div><p className="text-slate-400 text-sm leading-relaxed font-medium normal-case">{card.d}</p></div>
                     <div className="border-t border-white/5 pt-6 flex items-center gap-6"><span className={`text-5xl font-black ${card.c}`}>{card.stat}</span><span className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">{card.sub}</span></div>
                  </div>
               ))}
            </div>
         </div>
      </section>

      {/* 5. FOUNDERS CHOICE (REAL IMAGES) */}
      <section className="px-6 py-32 max-w-7xl mx-auto">
         <div className="text-center mb-20">
            <h2 className="text-5xl font-black mb-4 uppercase italic">Founders Choice</h2>
            <p className="text-slate-500 uppercase tracking-[0.4em] text-xs font-bold">Validated by builders</p>
         </div>
         <div className="grid md:grid-cols-3 gap-8">
            {[ { q: "Found my .com in seconds. The engine gave me better identities than naming agencies.", a: "Alex Rivers", r: "SaaS Founder", img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=100&h=100" }, { q: "NamerHQ found 'Lumea' for my app in 30 seconds. The linguistic filters are insane.", a: "Sarah Chen", r: "Creative Director", img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=100&h=100" }, { q: "Redline v4.3.2 doesn't just give names, it gives brand souls.", a: "Marcus Thorne", r: "E-com Entrepreneur", img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=100&h=100" } ].map((t, i) => (
               <div key={i} className="glass-panel p-10 rounded-[2.5rem] border-white/5 hover:border-[#DD2476]/30 transition-all">
                  <p className="text-slate-300 text-sm leading-loose mb-8 font-medium normal-case">"{t.q}"</p>
                  <div className="flex items-center gap-4">
                     <img src={t.img} alt={t.a} className="w-12 h-12 rounded-full border-2 border-[#DD2476]/20 object-cover" />
                     <div><div className="text-xs font-black text-white uppercase tracking-wider">{t.a}</div><div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{t.r}</div></div>
                  </div>
               </div>
            ))}
         </div>
      </section>

      {/* 6. FAQ */}
      <section className="px-6 py-20 max-w-4xl mx-auto">
         <h2 className="text-4xl font-black text-center mb-16 uppercase italic">Everything You Need to Know</h2>
         <div className="space-y-4">
            {[ { q: "How does the NamerHQ generator work?", a: "NamerHQ uses a linguistic mapping engine that blends semantic keywords with modern brand structures. Unlike random generators, we focus on phonetic appeal and 'stickiness' using our custom 2026 proprietary data set." }, { q: "Why is a .com domain important?", a: "The .com extension remains the 'gold standard' for trust and SEO. Most users instinctively type .com, and search engines prioritize these domains over newer extensions." }, { q: "How do I choose a catchy name?", a: "A great name should be easy to pronounce, under 3 syllables, and evocative of your mission. Avoid complex spellings that require correction when spoken aloud." }, { q: "Is this free?", a: "Yes, NamerHQ is 100% free. We earn a small commission if you choose to register your domain through our trusted partners, allowing us to keep this laboratory open." } ].map((faq, i) => (
               <div key={i} className="glass-panel p-8 rounded-2xl hover:bg-white/5 transition-colors cursor-pointer group">
                  <h3 className="font-bold text-lg mb-2 normal-case flex items-center justify-between">{faq.q} <ChevronDown className="w-5 h-5 text-[#DD2476] group-hover:rotate-180 transition-transform" /></h3>
                  <p className="text-slate-400 text-sm normal-case leading-relaxed">{faq.a}</p>
               </div>
            ))}
         </div>
      </section>

      {/* 7. LAB TEASER - OPTIMIZED FOR ALL DEVICES */}
<section className="px-4 md:px-6 pb-24 md:pb-32 max-w-7xl mx-auto">
   <div className="glass-panel p-8 md:p-20 rounded-[2.5rem] md:rounded-[4rem] text-center relative overflow-hidden">
      
      {/* Subtle Background Glow for Android Depth */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-[#DD2476]/10 blur-[80px] -z-10 opacity-50"></div>
      
      <div className="relative z-10">
          {/* Fluid Headline: 3xl on phone, 5xl or 6xl on PC */}
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black mb-10 md:mb-14 italic uppercase leading-tight tracking-tighter thermal-gradient-text">
            Naming Intelligence Lab
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 text-left max-w-5xl mx-auto mb-12 md:mb-16">
             
             {/* Card 1 */}
             <div className="bg-white/5 p-6 md:p-8 rounded-3xl border border-white/5 hover:border-[#DD2476]/30 transition-colors group">
                <h4 className="text-[#DD2476] font-black mb-3 flex items-center gap-3 uppercase tracking-wider text-xs md:text-sm">
                  <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform"/> 
                  Linguistic Engineering
                </h4>
                <p className="text-slate-400 text-[11px] md:text-xs leading-loose normal-case font-medium">
                  At NamerHQ, we view brand identity as a fusion of phonetics and psychology. Our engine uses mapping to ensure every result carries specific emotional weight for your target demographic.
                </p>
             </div>

             {/* Card 2 */}
             <div className="bg-white/5 p-6 md:p-8 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-colors group">
                <h4 className="text-purple-500 font-black mb-3 flex items-center gap-3 uppercase tracking-wider text-xs md:text-sm">
                  <Target className="w-4 h-4 group-hover:scale-110 transition-transform"/> 
                  Strategic Acquisition
                </h4>
                <p className="text-slate-400 text-[11px] md:text-xs leading-loose normal-case font-medium">
                  In 2026, the scarcity of .com domains makes the extension the primary trust anchor. We recommend vowel-dropped naming structures to secure high-value real estate.
                </p>
             </div>

          </div>

          {/* Call to Action Link */}
          <Link 
            to="/KnowledgeLab" 
            className="inline-flex items-center gap-3 border-b-2 border-[#DD2476] pb-1 text-[#DD2476] hover:text-white hover:border-white transition-all font-black uppercase tracking-[0.2em] text-xs md:text-sm"
          >
            Enter Laboratory <ArrowRight className="w-4 h-4"/>
          </Link>
      </div>
   </div>
</section>

 {/* READY TO FIND - OPTIMIZED FOR PC & ANDROID */}
<section className="relative bg-gradient-to-br from-pink-600 to-purple-900 mx-4 md:mx-auto max-w-7xl px-6 py-16 md:p-24 rounded-[3rem] md:rounded-[5rem] text-center italic font-black uppercase shadow-2xl mb-40 overflow-hidden">
    
    {/* Decorative background stars for mobile depth */}
    <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
        <Stars className="absolute top-10 left-10 w-20 h-20 rotate-12" />
        <Stars className="absolute bottom-10 right-10 w-20 h-20 -rotate-12" />
    </div>

    <div className="relative z-10">
        <Stars className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-6 md:mb-8 text-white animate-pulse" />
        
        {/* Responsive Heading: text-4xl on Android, text-7xl on PC */}
        <h2 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black mb-6 md:mb-8 leading-[1.1] md:leading-none text-white tracking-tighter italic uppercase">
            Ready to Find Your<br className="hidden md:block"/> Perfect Name?
        </h2>
        
        <p className="text-white/90 text-xs md:text-sm mb-10 md:mb-16 max-w-3xl mx-auto tracking-widest leading-relaxed font-bold">
            Join thousands of founders who discovered their brand identity with NamerHQ. 
            <span className="block mt-2 md:inline md:mt-0"> It's free, instant, and powered by linguistic intelligence.</span>
        </p>
        
        <button 
            onClick={() => navigate('/generator')} 
            className="w-full sm:w-auto bg-white text-pink-600 px-8 md:px-16 py-5 md:py-10 rounded-2xl md:rounded-[3rem] text-[10px] md:text-sm font-black uppercase tracking-[0.2em] md:tracking-[0.4em] hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-4 mx-auto shadow-xl"
        >
            Start Generating Now <ArrowRight className="w-5 h-5 md:w-6 md:h-6"/>
        </button>
        
        <p className="mt-8 text-[9px] md:text-[10px] text-white/50 font-black tracking-widest">
            No signup required • 100% free • Instant results
        </p>
    </div>
</section>

       {/* --- FOOTER --- */}
      <footer className="py-20 border-t border-white/5 px-10 bg-black font-bold italic text-left">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-20">
          <div className="col-span-1 md:col-span-2 space-y-6">
            <span className="text-2xl font-black uppercase text-white tracking-tighter">Namer<span className="text-red-600">HQ</span></span>
            <p className="text-slate-500 text-sm leading-relaxed max-w-xs italic font-bold">The world's most advanced linguistic engine for modern brand identities. Developed by NamerHQ.</p>
            <div className="inline-block px-3 py-1 rounded-full border border-red-600/30 bg-red-600/5 text-[9px] text-red-500 uppercase tracking-widest font-black">Affiliate Disclosure Applied</div>
          </div>
          <div className="space-y-8">
             <h4 className="text-[10px] font-black uppercase text-white tracking-[0.3em]">Trust & Connection</h4>
             <ul className="text-xs text-slate-500 space-y-5 uppercase italic font-bold">
                <li onClick={() => setActiveModal('Legal')} className="hover:text-green-500 cursor-pointer transition-colors">Privacy & Terms</li>
                <li onClick={() => setActiveModal('Contact')} className="hover:text-red-600 cursor-pointer transition-colors">Contact Us</li>
                <Link to="/KnowledgeLab" className="hover:text-green-500 cursor-pointer block transition-colors">Knowledge Lab</Link>
             </ul>
          </div>
          <div className="space-y-8">
             <h4 className="text-[10px] font-black uppercase text-white tracking-[0.3em]">System Status</h4>
             <div className="space-y-3 font-black italic">
                <div className="flex items-center gap-3"><div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,1)]" /><span className="text-xs uppercase text-slate-300 font-bold tracking-widest">Operational</span></div>
                <p className="text-[10px] text-slate-700 uppercase leading-loose tracking-widest italic">Latency: 0.1ms • Global CDN Active • SSL Encrypted</p>
             </div>
          </div>
        </div>
      </footer>

      {/* --- MODALS (CONTACT & LEGAL RESTORED WORD-FOR-WORD) --- */}
      {activeModal === 'Contact' && (
        <div className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl flex items-center justify-center p-6 text-left" onClick={() => setActiveModal(null)}>
          <div className="glass-card max-w-lg w-full p-10 rounded-[3rem] relative animate-reveal shadow-2xl" onClick={e => e.stopPropagation()}>
            <button onClick={() => setActiveModal(null)} className="absolute top-8 right-8 text-slate-500 hover:text-white transition-transform hover:rotate-90"><X /></button>
            <div className="flex items-center gap-3 mb-8"><Mail className="text-red-600 w-6 h-6" /><h2 className="text-2xl text-white font-black uppercase italic tracking-tighter leading-none">Global Support</h2></div>
            <p className="text-slate-400 text-sm italic font-bold mb-8 leading-relaxed italic">For research inquiries, technical support, or partnership proposals, please reach out via our official communication channel.</p>
            <div className="p-8 bg-white/5 rounded-3xl border border-white/5 mb-6 text-center shadow-inner">
                <h4 className="text-slate-500 text-[10px] uppercase mb-1 tracking-widest italic font-bold">Laboratory Email</h4>
                <a href="mailto:itsnamerhq@gmail.com" className="text-xl font-black text-red-600 hover:text-white transition-colors underline italic">itsnamerhq@gmail.com</a>
            </div>
            <div className="flex justify-between items-center bg-white/5 p-4 rounded-2xl border border-white/5 font-black uppercase text-[10px] italic">
                <span className="text-slate-500">Response Time</span>
                <span className="text-white tracking-widest">24-48 Business Hours</span>
            </div>
          </div>
        </div>
      )}

      {activeModal === 'Legal' && (
        <div className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl flex items-center justify-center p-6 text-left" onClick={() => setActiveModal(null)}>
          <div className="glass-card max-w-3xl w-full p-10 rounded-[3rem] relative overflow-y-auto max-h-[85vh] legal-box animate-reveal shadow-2xl" onClick={e => e.stopPropagation()}>
            <button onClick={() => setActiveModal(null)} className="absolute top-8 right-8 text-slate-500 hover:text-white transition-transform hover:rotate-90"><X /></button>
            <div className="flex items-center gap-3 mb-4"><FileText className="text-red-600 w-6 h-6" /><h2 className="text-2xl text-white font-black uppercase italic tracking-tighter leading-none italic">📜 Terms of Service, Privacy Policy & Advertising Disclosure</h2></div>
            <p className="text-[10px] uppercase font-bold italic text-slate-500 mb-8 border-b border-white/5 pb-4 tracking-widest">Last updated: January 2026</p>
            <div className="italic font-bold text-xs leading-relaxed space-y-8 text-slate-400 text-left uppercase">
                <section><h3 className="text-white mb-2">1. About NamerHQ</h3><p>Online tool for creative naming inspiration. All results are suggestions only and should be verified.</p></section>
                <section><h3 className="text-white mb-2">2. Terms of Service</h3><p>NamerHQ does not provide professional advice. Trademark verification is the responsibility of the user.</p></section>
                <section><h3 className="text-white mb-2">3. Privacy Policy</h3><p>Anonymous usage data is used for performance optimization. We do not collect identifiable personal information.</p></section>
                <section><h3 className="text-white mb-2">4. Advertising Disclosure</h3><p>NamerHQ uses Google AdSense. Advertising cookies enable serving ads based on your visit to NamerHQ and other sites. Users may opt out via <a href="https://adssettings.google.com" className="text-red-600 underline">Google Ad Settings</a>.</p></section>
                <section><h3 className="text-white mb-2">5. Cookie Policy</h3><p>Cookies are small text files stored on your device to improve functionality. Disabling them may affect site features.</p></section>
                <section><h3 className="text-white mb-2">6. Third-Party Services</h3><p>Services like Google AdSense collect data according to their own policies. NamerHQ is not responsible for their data practices.</p></section>
                <section><h3 className="text-white mb-2">7. Policy Updates</h3><p>We may update these terms at any time. Continued use of NamerHQ after updates means you accept the new terms.</p></section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- [MAIN APP ROUTER] ---
const MainApp = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/generator" element={<Generator />} />
        <Route path="/KnowledgeLab" element={<KnowledgeLab />} />
      </Routes>
      <SpeedInsights /><Analytics />
    </Router>
  );
};

export default MainApp;